import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Tooltip,
  Snackbar,
  Container,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  NetworkCheck as NetworkIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon,
  PersonOutline as PersonOutlineIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { organizationsApi } from '../../services/api';
import CreateManagerModal from './CreateManagerModal';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const OrganizationDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [createManagerModalOpen, setCreateManagerModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchOrganizationDetails();
  }, [id]);

  const fetchOrganizationDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await organizationsApi.getAdminDetails(id);
      setOrganizationDetails(response.data);
    } catch (err) {
      console.error('Error fetching organization details:', err);
      setError(err.response?.data?.error || 'Failed to fetch organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminIcon color="error" />;
      case 'manager': return <SecurityIcon color="warning" />;
      case 'inspector': return <AssessmentIcon color="info" />;
      case 'viewer': return <VisibilityIcon color="action" />;
      default: return <PersonOutlineIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'inspector': return 'info';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const handleManagerCreated = (data) => {
    setSuccessMessage(`Manager ${data.manager.full_name} created successfully!`);
    // Refresh organization details to show updated user counts
    fetchOrganizationDetails();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading organization details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/organizations')}>
            Back to Organizations
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!organizationDetails) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning">Organization not found</Alert>
      </Container>
    );
  }

  const { organization, userStats, networkStats, recentActivity, permissionDistribution } = organizationDetails;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/organizations')} sx={{ mr: -1 }}>
          <ArrowBackIcon />
        </IconButton>
        <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {organization.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organization details and analytics
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
          >
            Edit Organization
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            color="primary"
            onClick={() => setCreateManagerModalOpen(true)}
          >
            Create Manager
          </Button>
        </Stack>
      </Stack>

      {/* Organization Info Cards */}
      <Grid container spacing={3} mb={3}>
        {/* Basic Info Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 1, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Organization Information
              </Typography>
            </Stack>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1" fontWeight="bold">
                {organization.name}
              </Typography>
            </Box>
            {organization.country && (
              <Box display="flex" alignItems="center" mb={1}>
                <PublicIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {organization.country}
                </Typography>
              </Box>
            )}
            {organization.timezone && (
              <Box display="flex" alignItems="center" mb={1}>
                <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {organization.timezone}
                </Typography>
              </Box>
            )}
            {organization.contact_email && (
              <Box display="flex" alignItems="center" mb={1}>
                <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {organization.contact_email}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(organization.created_at)}
            </Typography>
            {organization.created_by_name && (
              <Typography variant="body2" color="text.secondary">
                Created by: {organization.created_by_name} ({organization.created_by_username})
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Quick Stats Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 1, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <AssessmentIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Quick Statistics
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary.main">
                    {userStats.total_users}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h3" color="secondary.main">
                    {networkStats.network_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Networks
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main">
                    {networkStats.section_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sections
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main">
                    {networkStats.asset_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assets
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabbed Content */}
      <Paper sx={{ borderRadius: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="User Analytics" />
            <Tab label="Network Overview" />
            <Tab label="Recent Activity" />
            <Tab label="Permissions" />
          </Tabs>
        </Box>

        {/* Tab 1: User Analytics */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* User Role Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 1, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <PeopleIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    User Role Distribution
                  </Typography>
                </Stack>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">Managers</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {userStats.manager_count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(userStats.manager_count / userStats.total_users) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                    color="warning"
                  />
                </Box>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">Inspectors</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {userStats.inspector_count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(userStats.inspector_count / userStats.total_users) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                    color="info"
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">Viewers</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {userStats.viewer_count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(userStats.viewer_count / userStats.total_users) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Permission Scope Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 1, height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Permission Scope Distribution
                  </Typography>
                </Stack>
                <Box mb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">Organization-wide Access</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {userStats.org_scope_count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(userStats.org_scope_count / userStats.total_users) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                    color="success"
                  />
                </Box>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">Limited Network/Section Access</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {userStats.limited_scope_count}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(userStats.limited_scope_count / userStats.total_users) * 100} 
                    sx={{ height: 8, borderRadius: 1 }}
                    color="secondary"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* User Status Metrics */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    User Status Metrics
                  </Typography>
                </Stack>
                <Grid container spacing={4}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <VerifiedIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4">{userStats.verified_users}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email Verified
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                      <Typography variant="h4">{userStats.active_users}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active (30 days)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="primary.main">
                        {Math.round((userStats.verified_users / userStats.total_users) * 100)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Verification Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="success.main">
                        {Math.round((userStats.active_users / userStats.total_users) * 100)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Activity Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Network Overview */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', height: '100%' }}>
                <NetworkIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h3" color="primary.main">
                  {networkStats.network_count}
                </Typography>
                <Typography variant="h6">Networks</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Total network infrastructures managed
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', height: '100%' }}>
                <AssessmentIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h3" color="secondary.main">
                  {networkStats.section_count}
                </Typography>
                <Typography variant="h6">Sections</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Network sections for detailed management
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', height: '100%' }}>
                <BusinessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h3" color="success.main">
                  {networkStats.asset_count}
                </Typography>
                <Typography variant="h6">Assets</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Total assets under management
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 3: Recent Activity */}
        <TabPanel value={activeTab} index={2}>
          <Paper sx={{ p: 3, borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <TrendingUpIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Recent User Activity
              </Typography>
            </Stack>
            {recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No recent activity found
              </Typography>
            ) : (
              <List>
                {recentActivity.map((activity, index) => (
                  <ListItem key={index} divider={index < recentActivity.length - 1}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getRoleIcon(activity.role)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {activity.full_name || 'Unknown User'}
                          </Typography>
                          <Chip 
                            label={activity.role} 
                            size="small" 
                            color={getRoleColor(activity.role)}
                          />
                        </Box>
                      }
                      secondary={`Last login: ${formatDate(activity.last_login)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </TabPanel>

        {/* Tab 4: Permissions */}
        <TabPanel value={activeTab} index={3}>
          <Paper sx={{ p: 3, borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Permission Distribution by Network/Section
              </Typography>
            </Stack>
            {permissionDistribution.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                No network/section specific permissions configured
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Network</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell align="center">Users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissionDistribution.map((permission, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {permission.network_name || 'All Networks'}
                        </TableCell>
                        <TableCell>
                          {permission.section_name || 'All Sections'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={permission.user_count} 
                            color="primary" 
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </TabPanel>
      </Paper>

      {/* Create Manager Modal */}
      <CreateManagerModal
        open={createManagerModalOpen}
        onClose={() => setCreateManagerModalOpen(false)}
        onSuccess={handleManagerCreated}
        organization={organization}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationDetailsPage; 